import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { DesktopAudioArtifactRef } from "@/modules/electron/types";
import { WebAudioArtifactRef } from "@/modules/audio-recording/web-artifacts";
import { AudioRecordingStrategy, normalizeAudioRecordingStrategy } from "@/store/audio-recordings";
import {
  AudioRecordingRef,
  AudioUploadPayloadProvider,
  PreparedAudioUploadResult,
} from "@/store/audio/uploading/audio-uploading.types";
import { RendererAudioUploadSource } from "@/store/audio/uploading/renderer-audio-upload-source";

type RecordingStrategyAudioUploadPayloadProviderConfig = {
  recordings: AudioRecordingRepository;
  webPayloadProvider: AudioUploadPayloadProvider<WebAudioArtifactRef>;
  desktopPayloadProvider: AudioUploadPayloadProvider<DesktopAudioArtifactRef>;
};

export class RecordingStrategyAudioUploadPayloadProvider implements AudioUploadPayloadProvider<RendererAudioUploadSource> {
  private readonly recordings: AudioRecordingRepository;
  private readonly webPayloadProvider: AudioUploadPayloadProvider<WebAudioArtifactRef>;
  private readonly desktopPayloadProvider: AudioUploadPayloadProvider<DesktopAudioArtifactRef>;

  constructor({
    recordings,
    webPayloadProvider,
    desktopPayloadProvider,
  }: RecordingStrategyAudioUploadPayloadProviderConfig) {
    this.recordings = recordings;
    this.webPayloadProvider = webPayloadProvider;
    this.desktopPayloadProvider = desktopPayloadProvider;
  }

  async prepareUploadPayload({
    audioRecordingId,
  }: AudioRecordingRef): Promise<PreparedAudioUploadResult<RendererAudioUploadSource>> {
    const recording = await this.recordings.getById({ id: audioRecordingId });
    if (!recording) {
      return {
        success: false,
        error: "Recording not found",
        shouldPersistError: false,
      };
    }

    const recordingStrategy = normalizeAudioRecordingStrategy({
      recordingStrategy: recording.data.value_json?.source_kind,
    });

    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return this.desktopPayloadProvider.prepareUploadPayload({ audioRecordingId });
    }

    return this.webPayloadProvider.prepareUploadPayload({ audioRecordingId });
  }
}
